import { jsPDF } from 'jspdf'
import { faGlobe, faShareAlt, faMobileAlt, faMicrophone, faQuestionCircle, faDesktop, faTv, faTabletAlt, faQuestion } from '@fortawesome/free-solid-svg-icons'
import { faAndroid, faApple, faAmazon } from '@fortawesome/free-brands-svg-icons'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import { AudienceParams } from '@/pdf/types'
import { renderHtmlToImage, getLogoDataUrl } from '@/pdf/htmlRenderer'

function escapeHtml(str: unknown): string {
    const s = String(str ?? '')
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
}

const ICON_MAP: Record<string, IconDefinition> = {
    'Site': faGlobe,
    'Agregadores': faShareAlt,
    'Aplicativo': faMobileAlt,
    'Assistentes de Voz': faMicrophone,
    'Outros': faQuestionCircle,
    'Android': faAndroid,
    'Iphone': faApple,
    'Desktop': faDesktop,
    'Alexa': faAmazon,
    'SmartTV': faTv,
    'Tablet': faTabletAlt,
    'Navegador': faGlobe,
    'VLC': faDesktop,
}

function iconSvg(label: string, size = 13): string {
    const def = ICON_MAP[label] ?? faQuestion
    const [w, h, , , path] = def.icon
    const d = Array.isArray(path) ? path[1] : path
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${size}" height="${size}" fill="#2e5eaa">${
        Array.isArray(path)
            ? `<path d="${path[0]}" opacity="0.4"/><path d="${path[1]}"/>`
            : `<path d="${d}"/>`
    }</svg>`
}

// ─── HTML builders ────────────────────────────────────────────────────────────

function barRowHtml(item: { label: string; percentual: number }): string {
    const fillW = Math.min(Math.max(item.percentual, 0), 100).toFixed(1)
    return `
    <div style="display:flex;align-items:center;margin-bottom:13px;gap:8px;">
        <div style="width:24px;height:24px;min-width:24px;background:#dae6f7;border-radius:4px;display:flex;align-items:center;justify-content:center;">${iconSvg(item.label)}</div>
        <span style="width:130px;font-size:12px;color:#373737;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(item.label)}</span>
        <div style="flex:1;height:8px;background:#e0e0e0;border-radius:4px;overflow:hidden;">
            <div style="height:100%;width:${fillW}%;background:#2e5eaa;border-radius:4px;"></div>
        </div>
        <span style="width:44px;text-align:right;font-size:11px;font-weight:bold;color:#6e6e6e;">${item.percentual.toFixed(1)}%</span>
    </div>`
}

function sectionHtml(
    title: string,
    items: { label: string; percentual: number }[],
    insight?: string,
): string {
    const rows = items.map(barRowHtml).join('')
    const insightHtml = insight
        ? `<div style="background:#e5eaf5;border-radius:5px;padding:10px 14px 10px 18px;position:relative;margin-top:6px;margin-bottom:4px;">
               <div style="position:absolute;left:0;top:0;bottom:0;width:6px;background:#2e5eaa;border-radius:5px 0 0 5px;"></div>
               <p style="font-size:12px;color:#32405a;font-style:italic;margin:0;line-height:1.5;">${escapeHtml(insight)}</p>
           </div>`
        : ''
    return `
    <div style="margin-bottom:32px;">
        <div style="margin-bottom:16px;">
            <div style="font-size:18px;font-weight:bold;color:#2e5eaa;font-family:Arial,sans-serif;padding-bottom:4px;">${escapeHtml(title)}</div>
            <div style="width:57px;height:3px;background:#2e5eaa;border-radius:2px;"></div>
        </div>
        ${rows}
        ${insightHtml}
    </div>`
}

function buildDistributionHtml(parametros: AudienceParams, logoSrc: string): string {
    const agora = new Date()
    const dataGerado = agora.toLocaleDateString('pt-BR')
    const horaGerado = agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    const nomeRadio = parametros.companyName || ''
    const hasPlatform = (parametros.distribuicao_plataforma?.dados?.length ?? 0) > 0
    const hasOrigin = (parametros.distribuicao_origem?.dados?.length ?? 0) > 0

    let sectionsHtml = ''

    if (hasPlatform) {
        const items = parametros.distribuicao_plataforma!.dados.map(d => ({
            label: d.plataforma ?? d.origem ?? '',
            percentual: d.percentual,
        }))
        sectionsHtml += sectionHtml('Sessões por plataforma', items, parametros.distribuicao_plataforma!.insight)
    }

    if (hasOrigin) {
        const items = parametros.distribuicao_origem!.dados.map(d => ({
            label: d.origem ?? d.plataforma ?? '',
            percentual: d.percentual,
        }))
        sectionsHtml += sectionHtml('Sessões por origem', items, parametros.distribuicao_origem!.insight)
    }

    return `
<div style="width:794px;height:1122px;background:#fafafa;font-family:Arial,sans-serif;padding:83px 57px 40px;box-sizing:border-box;position:relative;">
    ${logoSrc ? `<img src="${logoSrc}" style="position:absolute;top:20px;right:57px;height:22px;" />` : ''}

    ${sectionsHtml}

    <div style="position:absolute;bottom:24px;left:57px;right:57px;text-align:center;">
        <span style="font-size:9px;color:#a0a0a0;">Relat&oacute;rio gerado em ${dataGerado} &agrave;s ${horaGerado}${nomeRadio ? ` - ${nomeRadio}` : ''}</span>
    </div>
</div>
`
}

// ─── Page entry point ─────────────────────────────────────────────────────────

export async function addDistributionPage(pdf: jsPDF, parametros: AudienceParams): Promise<void> {
    const hasPlatform = (parametros.distribuicao_plataforma?.dados?.length ?? 0) > 0
    const hasOrigin = (parametros.distribuicao_origem?.dados?.length ?? 0) > 0

    if (!hasPlatform && !hasOrigin) return

    const logoSrc = await getLogoDataUrl()
    const img = await renderHtmlToImage(buildDistributionHtml(parametros, logoSrc))
    pdf.addPage()
    pdf.addImage(img, 'JPEG', 0, 0, 210, 297)
}

